'use memo';

import type { UpdateLoginFlowBody } from '@ory/client';
import { useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { UseAuthMethodsResult } from '../hooks/useAuthMethods';
import { OryForm } from './Form';
type AuthMethodTabsProps = {
  methods: UseAuthMethodsResult;
  disabled?: boolean;
  onSubmit: (body: UpdateLoginFlowBody) => void;
  onActiveTabChange?: (tab: string) => void;
};
function getMethodTabValue(method: UseAuthMethodsResult['methods'][number]['method']): string {
  return method.type;
}

/**
 * 認証方法が複数ある場合にタブで切り替えるコンポーネント。
 * 単一方法の場合はタブなしでフォームのみ表示。
 * サインイン・再認証・SecureGuard など認証方法選択が必要な場面で共通利用する。
 */
export function AuthMethodTabs({
  methods,
  disabled,
  onSubmit,
  onActiveTabChange
}: AuthMethodTabsProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.signIn'
  });

  // UpdateLoginFlowBody は method を判別子とする共用体型のため、
  // spread で method を上書きすると TypeScript が型を合わせられない。
  // OidcSubmit が返す body には必要なフィールドが含まれているため安全。
  const ssoSubmitHandler = useCallback((body: UpdateLoginFlowBody) => {
    onSubmit({
      ...body,
      method: 'oidc'
    } as UpdateLoginFlowBody);
  }, [onSubmit]);
  if (!methods.hasMultipleMethods && methods.methods.length === 1) {
    const entry = methods.methods[0];
    const submitHandler = entry.method.type === 'sso' ? ssoSubmitHandler : onSubmit;
    return <OryForm<UpdateLoginFlowBody> ui={entry.ui} disabled={disabled} onSubmit={submitHandler} />;
  }
  if (methods.methods.length === 0) {
    return null;
  }
  const defaultTab = getMethodTabValue(methods.methods[0].method);
  return <Tabs defaultValue={defaultTab} className="w-full" onValueChange={onActiveTabChange}>
      <TabsList className="w-full gap-0 rounded-none border-b border-neutral-sub-border bg-transparent p-0">
        {methods.methods.map(({
        method
      }) => {
        const tabValue = getMethodTabValue(method);
        const label = method.type === 'password' ? t('tabs.password') : t('tabs.sso');
        return <TabsTrigger key={tabValue} value={tabValue} className={cn('flex-1 rounded-none border-b-2 border-transparent px-4 py-2.5', 'text-label text-neutral-muted-on-fill', 'transition-colors', 'data-[state=active]:border-primary-fill data-[state=active]:text-neutral-on-fill data-[state=active]:shadow-none')}>
              {label}
            </TabsTrigger>;
      })}
      </TabsList>

      {methods.methods.map(({
      method,
      ui
    }) => {
      const tabValue = getMethodTabValue(method);
      const submitHandler = method.type === 'sso' ? ssoSubmitHandler : onSubmit;
      return <TabsContent key={tabValue} value={tabValue} className="mt-4">
            <OryForm<UpdateLoginFlowBody> ui={ui} disabled={disabled} onSubmit={submitHandler} />
          </TabsContent>;
    })}
    </Tabs>;
}